import { useCallback } from "react";
import { Bot } from "lucide-react";
import { useDevAgentStore } from "@/stores/devAgentStore";
import { useShallow } from "zustand/react/shallow";
import { PanelSection } from "./PanelSection";

/** v4 Phase 10: Agents panel — shows tool calls + pipeline activity (not text) */
export function AgentsPanel() {
  const { feedItems, runStatus, pipelineSteps } = useDevAgentStore(
    useShallow((s) => ({
      feedItems: s.feedItems,
      runStatus: s.runStatus,
      pipelineSteps: s.pipelineSteps,
    }))
  );

  const toolCalls = feedItems
    .filter((f) => f.type === "tool_call")
    .slice()
    .reverse();
  const isRunning = runStatus === "running";
  const totalCount =
    toolCalls.length +
    (isRunning
      ? pipelineSteps.filter((s) => s.status === "running").length
      : 0);

  const TOOL_LABELS: Record<string, string> = {
    read_source_code: "Read code",
    get_class_detail: "Class detail",
    get_method_detail: "Method detail",
    get_object_fields: "Object fields",
    get_metadata_by_name: "Metadata lookup",
    retrieve_metadata: "Retrieve metadata",
    graph_impact: "Impact analysis",
    graph_dependents: "Dependents",
    graph_calls: "Call graph",
    graph_data_access: "Data access",
    graph_object_usage: "Object usage",
    graph_triggers_on: "Triggers",
    run_soql_query: "SOQL query",
    tooling_query: "Tooling query",
    run_apex_test: "Run tests",
    semantic_search: "Search",
    grep: "Code search",
    find_references: "Find references",
    validate_apex: "Validate",
    execute_code: "Execute code",
  };

  const clearAgents = useCallback(() => {
    const state = useDevAgentStore.getState();
    state.feedItems
      .filter((f) => f.type === "tool_call")
      .forEach((f) => state.removeFeedItem(f.id));
    state.clearPipelineSteps();
  }, []);

  return (
    <PanelSection
      title="Agents"
      icon={<Bot className="w-3.5 h-3.5" />}
      count={totalCount}
      onClear={clearAgents}
    >
      {isRunning && pipelineSteps.length > 0 && (
        <div className="space-y-1 mb-2">
          {pipelineSteps
            .filter((s) => s.status === "running")
            .map((step, i) => (
              <div key={i} className="flex items-center gap-2 py-0.5">
                <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse shrink-0" />
                <span className="text-xs text-blue-400">{step.label}</span>
              </div>
            ))}
        </div>
      )}
      {toolCalls.length > 0 ? (
        <div className="space-y-0.5">
          {toolCalls.map((item) => {
            const d = item.data;
            const args = d.args || d.arguments || {};
            const sobjectFromQuery = (q: unknown): string => {
              if (typeof q !== "string") return "";
              const m = q.match(/\bFROM\s+([A-Za-z0-9_]+)/i);
              return m ? m[1] : "";
            };
            const rawTarget =
              args.name ||
              args.class_name ||
              args.file_name ||
              args.object_name ||
              args.metadata_type ||
              args.test_class_names?.[0] ||
              sobjectFromQuery(args.query) ||
              args.pattern ||
              args.text ||
              Object.values(args).find(
                (v): v is string =>
                  typeof v === "string" &&
                  v.length > 0 &&
                  v.length < 60 &&
                  !v.includes(" "),
              ) ||
              "";
            const target =
              typeof rawTarget === "string"
                ? rawTarget
                    .replace(/\.(cls|trigger)$/, "")
                    .replace(/<[^>]*>/g, "")
                    .slice(0, 40)
                : "";
            const label =
              TOOL_LABELS[d.name] || d.name?.replace(/_/g, " ") || "tool";
            const isErr = d.status === "error";
            const isDone = d.status === "done";
            const duration = d.durationMs
              ? `${(d.durationMs / 1000).toFixed(1)}s`
              : "";
            return (
              <div
                key={item.id}
                className="flex items-center gap-2 py-0.5 text-xs"
              >
                <div
                  className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                    d.status === "running"
                      ? "bg-blue-400 animate-pulse"
                      : isErr
                        ? "bg-red-400"
                        : "bg-green-400"
                  }`}
                />
                <span
                  className={`truncate flex-1 ${isErr ? "text-red-400" : isDone ? "text-muted-foreground" : "text-foreground"}`}
                >
                  {label}
                  {target ? `: ${target}` : ""}
                </span>
                {duration && (
                  <span className="text-[10px] text-muted-foreground font-medium shrink-0">
                    {duration}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      ) : !isRunning ? (
        <div className="text-muted-foreground text-center py-2 text-xs">
          Agent activity appears here during runs
        </div>
      ) : null}
    </PanelSection>
  );
}
